import { useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import qs from "query-string";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import Modal from "../../components/Modal";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useAccessSettings } from "../../lib/hooks/useQueryBoard";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";

import {
  MUTATION_SWITCH_SHELLY_CONTROLLER,
  MUTATION_SWITCH_TUYA_CONTROLLER,
} from "../../config/graphql/mutation";

const AccessControl = () => {
  const { t } = useTranslation(["access"]);

  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();

  const { search } = useLocation();
  const query = qs.parse(search);
  const autoMode = query.autoMode === "true";

  const { activeAccess } = useAccessSettings();

  const [status, setStatus] = useState<"error" | "loading" | "success">();

  const onSwitch = async () => {
    setStatus("loading");

    try {
      await client.request(
        activeAccess === "shelly"
          ? MUTATION_SWITCH_SHELLY_CONTROLLER
          : MUTATION_SWITCH_TUYA_CONTROLLER,
        {
          input: {
            id,
            action: activeAccess === "shelly" ? "ON_WITH_DELAY" : "ON",
          },
        },
      );

      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  };

  useEffect(() => {
    if (autoMode) {
      onSwitch();
    }
  }, [autoMode]);

  const onDismiss = () => {
    if (status === "error") {
      setStatus(undefined);
      return;
    }

    navigate("/", { replace: true });
  };

  const timeout = useMemo(() => {
    if (status === "error") {
      return 7000;
    }

    if (status === "success") {
      return 4000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  return (
    <div className="d-flex align-items-center flex-column">
      <BackButton to="/" />
      <Title className="page-title home mb-5">
        <Typography variant="h1">
          {t(`access:control.title${autoMode ? "Auto" : ""}`)}
        </Typography>
        <Typography variant="h3" className="font-weight-regular mt-5">
          {t(`access:control.subtitle${autoMode ? "Auto" : ""}`)}
        </Typography>
      </Title>
      {!autoMode && (
        <Button
          variant="contained"
          className="mt-4 px-5"
          onClick={onSwitch}
          disabled={status === "loading"}
        >
          {t("access:control.openBtn")}
        </Button>
      )}
      {status && (
        <Modal
          status={status}
          title={<Trans i18nKey={`access:status.${status}.title`} />}
          message={<Trans i18nKey={`access:status.${status}.message`} />}
          onDismiss={onDismiss}
        />
      )}
    </div>
  );
};

export default AccessControl;
